// Owned by the shared community gallery panel; imported and re-exported here so the store,
// its actions, and DEFAULT_STATE keep importing it from this module.
import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'

export { DEFAULT_COMMUNITY_GALLERY_FILTERS }

// emailWhenReady is null until the user touches the toggle in this session; the hook then falls
// back to the server value from page config. See useNotificationPreferences. browserNotifications
// is device-local and defaults off until CleverPush confirms a subscription.
export const DEFAULT_NOTIFICATION_PREFERENCES = {
  emailWhenReady: null,
  browserNotifications: false,
}

export const PROVIDER = {
  TENCENT: 'tencent',
  MESHY: 'meshy',
  HYPER3D: 'hyper3d',
  TRIPO3D: 'tripo3d',
  // Image providers, used by the AI Assistant rather than the 3D generation form.
  FLUX: 'flux',
  NANO_BANANA: 'nano_banana',
  FAL: 'fal',
}

export const POLY_COUNT = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

export const FACE_LEVEL = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

export const MODEL_FORMAT_OUTPUT = {
  GLB: 'glb',
  FBX: 'fbx',
  OBJ: 'obj',
  STL: 'stl',
  USDZ: 'usdz',
}

export const MODEL_FORMAT_EXPORT = {
  GLB: 'glb',
  FBX: 'fbx',
  OBJ: 'obj',
  STL: 'stl',
  USDZ: 'usdz',
}

export const MODE = {
  NORMAL: 'normal',
  FAST: 'fast',
  NB2_LITE: 'nb2-lite',
}

export const GENERATE_TYPE = {
  NORMAL: 'normal',
  LOW_POLY: 'lowPoly',
  SKETCH: 'sketch',
  GEOMETRY: 'geometry',
}

export const USE_CASE = {
  STANDARD: 'standard',
  THREE_D_PRINT: 'threeDPrint',
  HERO: 'hero',
  PROP: 'prop',
  LOW_POLY: 'lowPoly',
  SHAPE_ONLY: 'shapeOnly',
}

export const HISTORY_VIEW = {
  GRID: 'grid',
  TREE: 'tree',
}

export const VIEWER_SIDEBAR_TAB = {
  REMESH: 'remesh',
  TEXTURE: 'texture',
  SEGMENT: 'segment',
  ANIMATE: 'animate',
  RENDER: 'render',
  VARIATIONS: 'variations',
  ASSIST: 'assist',
}

// Identifies which action button started the in-flight run, so only that button
// shows the in-progress label while the others are disabled. Initial generations
// use this sentinel; enhancement runs use their VIEWER_SIDEBAR_TAB id.
export const GENERATION_TRIGGER_KEY = 'generation'

export const MOBILE_PANEL = {
  GENERATE: 'generate',
  VIEWER: 'viewer',
  ENHANCE: 'enhance',
}

export const IMAGE_ASSISTANT_TAB = {
  SEARCH: 'search',
  CREATE: 'create',
  EDIT: 'edit',
}

export const MATERIAL = {
  PBR: 'pbr',
  BASIC: 'basic',
  GEOMETRY_ONLY: 'geometry_only',
}

export const TEXTURE_QUALITY = {
  TWO_K: '2k',
  FOUR_K: '4k',
  EIGHT_K: '8k',
}

export const POLYGON_TYPE = {
  QUADRILATERAL: 'quadrilateral',
  TRIANGLE: 'triangle',
}

export const POLYCOUNT_MODE = {
  TARGET: 'target',
  UNLIMITED: 'unlimited',
}

export const MODEL_ID = {
  TENCENT_TEXT_TO_3D_PRO: 'tencent-text-to-3d-pro',
  TENCENT_IMAGE_TO_3D_PRO: 'tencent-image-to-3d-pro',
  TENCENT_MESH_SIMPLIFICATION_PRO: 'tencent-mesh-simplification-pro',
  TENCENT_MESH_SEGMENTATION_PRO: 'tencent-mesh-segmentation-pro',
  TENCENT_RETEXTURE_PRO: 'tencent-retexture-pro',
  TRIPO3D_IMAGE_TO_3D_DEFAULT: 'tripo3d-image-to-3d-default',
  TRIPO3D_TEXT_TO_3D_DEFAULT: 'tripo3d-text-to-3d-default',
  TRIPO3D_RETEXTURE_DEFAULT: 'tripo3d-retexture-default',
  MESHY_TEXT_TO_3D_PRO: 'meshy-text-to-3d-pro',
  MESHY_IMAGE_TO_3D_PRO: 'meshy-image-to-3d-pro',
  MESHY_RETEXTURE_PRO: 'meshy-retexture-pro',
  MESHY_MESH_SIMPLIFICATION_PRO: 'meshy-mesh-simplification-pro',
  TRIPO3D_MESH_SIMPLIFICATION_DEFAULT: 'tripo3d-mesh-simplification-default',
  TRIPO3D_MESH_SEGMENTATION_DEFAULT: 'tripo3d-mesh-segmentation-default',
  HYPER3D_TEXT_TO_3D_GEN2: 'hyper3d-text-to-3d-gen2',
  HYPER3D_IMAGE_TO_3D_GEN2: 'hyper3d-image-to-3d-gen2',
  HYPER3D_RETEXTURE_PRO: 'hyper3d-retexture-pro',
  MESHY_ANIMATION_PRO: 'meshy-animation-pro',
  TRIPO3D_ANIMATION_DEFAULT: 'tripo3d-animation-default',
  NANO_BANANA_RENDER_3D_TO_2D_NB2_LITE: 'gemini-render-3d-to-2d-nb2-lite',
  NANO_BANANA_STYLE_APPLY_NB2_LITE: 'gemini-style-apply-nb2-lite',
  FLUX_RENDER_3D_TO_2D_FLUX2_PRO: 'bfl-render-3d-to-2d-flux2-pro',
  FLUX_STYLE_APPLY_FLUX2_PRO: 'bfl-style-apply-flux2-pro',
}

// The Render tab's runs: a plain render, or a style-apply when a style preset is
// selected. Both produce a 2D image that opens in the Render preview. The tab submits
// the Gemini models; the Flux ones are the backend's other ranked providers for the
// same actions, so a run created without a model_id can land on them.
export const RENDER_MODEL_IDS = new Set([
  MODEL_ID.NANO_BANANA_RENDER_3D_TO_2D_NB2_LITE,
  MODEL_ID.NANO_BANANA_STYLE_APPLY_NB2_LITE,
  MODEL_ID.FLUX_RENDER_3D_TO_2D_FLUX2_PRO,
  MODEL_ID.FLUX_STYLE_APPLY_FLUX2_PRO,
])

export const ACTION = {
  IMAGE_TO_3D: 'image-to-3d',
  TEXT_TO_3D: 'text-to-3d',
  CONVERSION: 'conversion',
  MESH_SEGMENTATION: 'mesh-segmentation',
  MESH_SIMPLIFICATION: 'mesh-simplification',
  RETEXTURE: 'retexture',
  UV_UNFOLDING: 'uv-unfolding',
  ANIMATION: 'animation',
  RENDER: 'render-3d-to-2d',
  STYLE_APPLY: 'style-apply',
}

export const SURFACE = {
  THREE_D_STUDIO: 'three_d_studio',
  IMAGE_GENERATOR: 'image_generator',
}

// Backend `action` values for the image runs the assistant drives - see
// Generator::Providers::Flux::Schemas::Catalog,
// Generator::Providers::NanoBanana::Schemas::Catalog and
// Generator::Providers::Fal::Schemas::Catalog. Flux declares every generation and edit
// action; nano-banana every one except inpaint. Background removal and upscale are the
// Edit toolbar tools, served by fal only, so their runs are locked to its model_id.
export const IMAGE_ACTION = {
  TEXT_TO_IMAGE: 'text-to-image',
  SIMILAR_TO: 'similar-to',
  IMAGE_EDIT: 'image-edit',
  MULTI_IMAGE_EDIT: 'multi-image-edit',
  INPAINT: 'inpaint',
  BACKGROUND_REMOVAL: 'background-removal',
  UPSCALE: 'upscale',
}

export const IMAGE_MODE = {
  FLUX2_PRO: 'flux2-pro',
  FLUX1_FILL: 'flux1-fill',
  NB2_LITE: 'nb2-lite',
  BRIA_RMBG2: 'bria-rmbg2',
  SEEDVR2: 'seedvr2',
}

export const IMAGE_MODEL_ID = {
  FLUX_TEXT_TO_IMAGE_FLUX2_PRO: 'bfl-text-to-image-flux2-pro',
  FLUX_SIMILAR_TO_FLUX2_PRO: 'bfl-similar-to-flux2-pro',
  FLUX_IMAGE_EDIT_FLUX2_PRO: 'bfl-image-edit-flux2-pro',
  FLUX_MULTI_IMAGE_EDIT_FLUX2_PRO: 'bfl-multi-image-edit-flux2-pro',
  FLUX_INPAINT_FLUX1_FILL: 'bfl-inpaint-flux1-fill',
  NANO_BANANA_TEXT_TO_IMAGE_NB2_LITE: 'gemini-text-to-image-nb2-lite',
  NANO_BANANA_SIMILAR_TO_NB2_LITE: 'gemini-similar-to-nb2-lite',
  NANO_BANANA_IMAGE_EDIT_NB2_LITE: 'gemini-image-edit-nb2-lite',
  NANO_BANANA_MULTI_IMAGE_EDIT_NB2_LITE: 'gemini-multi-image-edit-nb2-lite',
  FAL_BACKGROUND_REMOVAL_BRIA_RMBG2: 'fal-background-removal-bria-rmbg2',
  FAL_UPSCALE_SEEDVR2: 'fal-upscale-seedvr2',
}

// Every ratio both image catalogs accept. The composer offers a subset of them.
export const IMAGE_ASPECT_RATIOS = ['auto', '1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16']

// What a generated image is for. The backend picks the prompt template by it: the plain
// one for general, the 3D-input wrapper for 3d_input. Left out, it falls back to general,
// so every run that declares it sends it explicitly.
export const OUTPUT_PURPOSE = {
  GENERAL: 'general',
  THREE_D_INPUT: '3d_input',
}
export const OUTPUT_PURPOSES = [OUTPUT_PURPOSE.GENERAL, OUTPUT_PURPOSE.THREE_D_INPUT]

// Providers intentionally hidden from the AI Engine selector for a given enhanced
// action. Hidden providers are also excluded from availableEnhancedProviders so they
// can never become the auto-selected active provider while their button is not shown.
export const HIDDEN_ENHANCED_PROVIDERS_BY_ACTION = {
  [ACTION.MESH_SIMPLIFICATION]: [PROVIDER.TENCENT],
  [ACTION.MESH_SEGMENTATION]: [PROVIDER.MESHY],
  [ACTION.ANIMATION]: [PROVIDER.TENCENT],
}

export const LIBRARY_FILTER = {
  GENERATED_ALL: 'generated_all',
  GENERATED_FAVORITES: 'generated_favorites',
  GENERATED_DRAFTS: 'generated_drafts',
  PURCHASED: 'purchased',
  UPLOADED: 'uploaded',
}

// Definition order is the slot order in the multi-view uploader and the order the
// views are sent in input_image, front first.
export const VIEW_TYPE = {
  FRONT: 'front',
  LEFT: 'left',
  RIGHT: 'right',
  BACK: 'back',
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT_FRONT: 'left_front',
  RIGHT_FRONT: 'right_front',
}

export const TEXTURE_TAB = {
  GENERATE: 'generate',
  RESOLUTION: 'resolution',
  EDIT: 'edit',
}

export const TEXTURE_ACTION_TAB = {
  IMAGE: 'image',
  TEXT: 'text',
}

export const DEFAULT_SCHEMA = {
  generationAction: ACTION.IMAGE_TO_3D,
  generationProvider: undefined,
  generationMode: MODE.NORMAL,
  generationModelSchema: {},
  availableGenerationProviders: [],
  enhancedAction: ACTION.MESH_SIMPLIFICATION,
  enhancedProvider: PROVIDER.TENCENT,
  enhancedMode: MODE.NORMAL,
  enhancedModelSchema: {},
  availableEnhancedProviders: [],
}

export const DEFAULT_MODEL_SETTINGS = {
  prompt: '',
  polyCount: POLY_COUNT.HIGH,
  generateType: GENERATE_TYPE.NORMAL,
  inputImage: undefined,
  inputImageUrl: undefined,
  // Multi-view uploads keyed by VIEW_TYPE, kept apart from the single-image inputImage.
  isMultiView: false,
  inputViewImages: {},
  inputThreeDUrl: undefined,
  isPrivate: false,
  selectedUseCase: USE_CASE.STANDARD,
  extraQuality: false,
  // default values for the standard use case
  isSmartLowPoly: false,
  isUnlimitedPolycount: false,
  material: MATERIAL.PBR,
  polygonType: POLYGON_TYPE.TRIANGLE,
  // Empty until the first schema resolves; setGenerationModelSchema then snaps it
  // to the tier bound named by polyCount, so the initial High is provider-aware.
  targetPolycount: '',
}

// Client-visible generation stages. Mirrors Generator::Runs::DisplayPhase on the
// backend: queued/analyzing/generating/finalizing come from the run's phase enum,
// refining is derived server-side when a run stays in generating past the estimate.
export const GENERATION_PHASE = {
  QUEUED: 'queued',
  ANALYZING: 'analyzing',
  GENERATING: 'generating',
  REFINING: 'refining',
  FINALIZING: 'finalizing',
}

export const SETTINGS_MODAL_TAB = {
  USAGE: 'usage',
  NOTIFICATIONS: 'notifications',
}

export const DEFAULT_UI = {
  activeViewerSidebarTab: undefined,
  activeTextureTab: TEXTURE_TAB.GENERATE,
  activeTextureActionTab: TEXTURE_ACTION_TAB.IMAGE,
  activeMobilePanel: MOBILE_PANEL.GENERATE,
  isSidebarOpen: true,
  // False until useInitialMobilePanel has decided the sidebar's starting state. The server
  // renders it open; on phones it collapses on the first client effect, and the sidebar
  // looks collapsed until then (sidebar--pending-collapse).
  isInitialSidebarStateSettled: false,
  isModelSettingsOpen: true,
  isViewerSidebarOpen: false,
  isImageAssistantOpen: false,
  isStylePanelOpen: false,
  // The style picker of the image composers (AI Assistant Create tab, Image Generator page
  // and its editor), shown as a modal. Separate from isStylePanelOpen, which the Render
  // tab's flyout owns.
  isImageStyleModalOpen: false,
  isHistoryDetached: false,
  historyView: HISTORY_VIEW.GRID,
  imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
  // Generator session the AI Assistant's image runs live in. Owned by the ui slice
  // rather than borrowed from generation.sessionId, which the 3D run lifecycle resets
  // (addActiveRun, setInputImage, setInputThreeD), silently losing the conversation.
  imageAssistantSessionId: undefined,
  // Generator session of the Image Generator page's conversation. Kept apart from
  // imageAssistantSessionId so a chat started in the /ai panel is not restored there.
  imageGeneratorSessionId: undefined,
  // Bumped to start the Image Generator page over: its Create tab remounts on it, which
  // drops a conversation that lives in component state rather than in the store.
  imageGeneratorResetKey: 0,
  // The AI Assistant composer's Private switch, independent of the 3D panel's
  // modelSettings.isPrivate. Resets with the panel, like the rest of its state.
  isImageAssistantPrivate: false,
  editImage: null,
  // Image opened on the full-width editor, which is a second surface over the same
  // editor components. Deliberately separate from editImage / imageAssistantSessionId:
  // those belong to the AI Assistant panel, and sharing them would put two editors on
  // one image whenever both are mounted.
  openedImage: null,
  openedImageSessionId: undefined,
  openedImageRunId: null,
  // The run behind the version on the editor's canvas, which moves with each edit and
  // History pick. The library highlights its card. Null when that run is unknown.
  openedImageCanvasRunId: null,
  openedImageCanReturnToCreate: false,
  // The address bar the Image Generator's Create page showed before an opened image's URL
  // replaced it, restored when the editor's Back returns there. Null when nothing was replaced.
  openedImageReturnUrl: null,
  // A finished Render tab run, shown in the Render preview panel: the output image and the
  // session its run lives in, so edits made there join it. Null when no render is open.
  renderPreview: null,
  // Asks the library to switch to a tab ({ tab }). A fresh object per request, so asking for
  // the tab already requested still applies after the user picked another one by hand.
  libraryTabRequest: null,
  isAdvancedSettingsExpanded: false,
  openedModel: {},
  // Slug in the current path that names a purchased marketplace model rather than a run.
  acquiredSlug: null,
  isModelUploading: false,
  isImageUploading: false,
  isUsageModalOpen: false,
  usageModalReason: null,
  isSettingsModalOpen: false,
  settingsModalTab: SETTINGS_MODAL_TAB.USAGE,
  isCommentsModalOpen: false,
  isThreeDModelPickerOpen: false,
  isImageLightboxOpen: false,
  pendingGenerationTrigger: null,
  pendingPostAuthGenerationTrigger: null,
  isSignInModalOpen: false,
  signInModalMode: 'signup',
  signInModalStep: 'auth',
  // The BE takes one feedback per user per campaign, so a submit hides the form for every
  // run until the next page load, when pageConfig.feedbackEligible reflects it.
  isFeedbackSubmitted: false,
}

export const STUDIO_ACCESS_TIER = {
  NOT_SUBSCRIBED: 'not_subscribed',
  IN_FREE_BETA: 'in_free_beta',
  SUBSCRIBED: 'subscribed',
  ELIGIBLE_FOR_TRIAL: 'eligible_for_trial',
  MUST_JOIN_WAITLIST: 'must_join_waitlist',
  WAITLISTED: 'waitlisted',
  HAS_CREDITS: 'has_credits',
  HAS_REWARD_CREDITS: 'has_reward_credits',
}

// Free users who generate on spendable credits. They get the credits usage view and the
// paid-tier concurrency presentation, but none of the premium features.
export const CREDITS_STUDIO_ACCESS_TIERS = new Set([
  STUDIO_ACCESS_TIER.HAS_CREDITS,
  STUDIO_ACCESS_TIER.HAS_REWARD_CREDITS,
])

// Tiers that cannot start a generation yet: clicking a generate control opens the
// usage modal (start the free trial, subscribe, join the waitlist) instead of
// running. Shared by the 3D generate button and the AI Assistant composers.
export const MODAL_STUDIO_ACCESS_TIERS = new Set([
  STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL,
  STUDIO_ACCESS_TIER.MUST_JOIN_WAITLIST,
  STUDIO_ACCESS_TIER.NOT_SUBSCRIBED,
  STUDIO_ACCESS_TIER.WAITLISTED,
])

// Why the usage modal was opened. PAID_FEATURE is set when a locked control
// (Private mode, DCC Bridge, Export) is clicked, so the modal shows the upgrade
// body with the advanced-features list regardless of the user's tier, instead of
// the tier-specific usage/quota body.
export const USAGE_MODAL_REASON = {
  PAID_FEATURE: 'paid_feature',
}

export const DEFAULT_GENERATION = {
  // In-flight runs, in start order. Each entry: { clientId, runId, sessionId,
  // triggeredBy, isEnhancement, enhancingSessionId, startedAt, settings }.
  // The scalar fields below mirror the most recently started run for consumers
  // that only care about a single live run (banner, collapsed card, global nav).
  activeRuns: [],
  isGenerating: false,
  isEnhancing: false,
  triggeredBy: null,
  enhancingSessionId: undefined,
  hasGenerated: false,
  hasFailed: false,
  errorId: "",
  errorDetails: {},
  generationStartedAt: null,
  runId: undefined,
  sessionId: undefined,
  lastGenerationSettings: {},
  availableCredits: null,
  // Snapshot of the Generate panel settings (modelSettings + generation action/provider)
  // the last run was started with. While the panel still matches it, the Generate button
  // stays disabled so the same generation is not submitted twice by accident.
  lockedGenerationSettings: null,
}

export const DEFAULT_SUBSCRIPTION = {
  studioAccessTier: null,
  // Resolved server-side rather than derived from studioAccessTier: a migrated free trial is
  // entitled to the DCC bridge, private mode and export while still reading 'in_free_beta'.
  // null until hydrated, so consumers must coerce rather than test for false.
  premiumFeatures: null,
  monthlyGenerationsAvailable: 0,
  monthlyGenerationsUsed: 0,
  extraGenerationsAvailable: 0,
  extraGenerationsUsed: 0,
  subscriptionPrice: null,
  subscriptionOriginalPrice: null,
  subscriptionResetDate: null,
  extraGenerationsPrice: null,
  subscriptionMonthlyGenerations: null,
  subscriptionMonthlyModels: null,
  generationCost: 0,
  subscriptionPaymentFormUrl: null,
  concurrencyCap: null,
  // :enable_multiple_generations Flipper flag - gates the concurrent-generation UI
  // (additive generating cards, cap tooltips, crown upsell). Off = legacy single run.
  multipleGenerationsEnabled: false,
}

export const DEFAULT_REMESH_SETTINGS = {
  isSmartLowPoly: false,
  polygonType: POLYGON_TYPE.QUADRILATERAL,
  faceLevel: FACE_LEVEL.MEDIUM,
  targetPolycount: '',
}

export const DEFAULT_TEXTURE_SETTINGS = {
  inputImage: undefined,
  inputImageUrl: undefined,
  prompt: '',
  material: MATERIAL.PBR,
  enablePbr: true,
  textureQuality: TEXTURE_QUALITY.FOUR_K,
}

export const DEFAULT_ANIMATE_SETTINGS = {
  animationId: null,
}

// The render tab is a placeholder with no settings yet; its slice exists so
// per-model resets can already clear it once it gains state.
// The render schema takes one object image plus this many style references.
export const MAX_RENDER_STYLES = 5

export const DEFAULT_RENDER_SETTINGS = {
  prompt: '',
  // The chosen style presets from /api/internal/generator/style_presets
  // ({ id, slug, name, tags, image_url, asset_id }), in selection order.
  styles: [],
  // Sent as the render's `creativity`: how strongly the styles override the model's look.
  styleStrength: 0.5,
}

export const MAX_IMAGE_STYLES = 5

// The AI Assistant composer's generation settings, shared with the Image Generator page.
// modelId is the image provider that answers the run; variations is the number of images
// per run. Kept in the store so they outlive the panel, which remounts its tabs on close.
export const DEFAULT_IMAGE_SETTINGS = {
  modelId: PROVIDER.NANO_BANANA,
  aspectRatio: '1:1',
  variations: 1,
  // The chosen style presets from /api/internal/generator/style_presets, in selection
  // order, the same shape DEFAULT_RENDER_SETTINGS.styles holds.
  styles: [],
}

export const DEFAULT_VIEWER = {
  fileFormat: null,
  fileSize: null,
  inputThreeD: undefined,
  material: undefined,
  meshList: [],
  isRigged: false,
  explodedViewEnabled: false,
  segmentationRenderModeAvailable: false,
  selectedMeshId: null,
}

// Viewer-lifetime state kept in its own slice so per-model resets (setOpenedModel
// rebuilds the `viewer` slice from DEFAULT_VIEWER) can never wipe the live viewer
// instance the sidebar needs to drive segmentation/exploded view.
export const DEFAULT_STATIC_VIEWER = {
  instance: null,
  isSwapping: false,
  frozenStats: null,
}

export const DEFAULT_DCC_BRIDGE = {
  selectedSoftwareName: null,
  sendStatus: 'idle',
  sendingTo: null,
  isSendModalOpen: false,
  connectedSoftwares: {},
}

export const DEFAULT_EXPORT = {
  selectedFormat: MODEL_FORMAT_EXPORT.GLB,
  status: 'idle',
  activeConversion: null,
  downloadUrl: null,
}

export const DEFAULT_STATE = {
  // filters
  libraryFilter: [],
  // schema
  schema: { ...DEFAULT_SCHEMA },
  // modelSettings
  modelSettings: { ...DEFAULT_MODEL_SETTINGS },
  // textureSettings
  textureSettings: { ...DEFAULT_TEXTURE_SETTINGS },
  // animateSettings
  animateSettings: { ...DEFAULT_ANIMATE_SETTINGS },
  // renderSettings
  renderSettings: { ...DEFAULT_RENDER_SETTINGS },
  // imageSettings
  imageSettings: { ...DEFAULT_IMAGE_SETTINGS },
  // generation
  generation: { ...DEFAULT_GENERATION },
  // subscription
  subscription: { ...DEFAULT_SUBSCRIPTION },
  // remeshSettings
  remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
  // ui
  ui: { ...DEFAULT_UI },
  // viewer
  viewer: { ...DEFAULT_VIEWER },
  // staticViewer
  staticViewer: { ...DEFAULT_STATIC_VIEWER },
  // dccBridge
  dccBridge: { ...DEFAULT_DCC_BRIDGE },
  // export
  export: { ...DEFAULT_EXPORT },
  // communityGalleryFilters
  communityGalleryFilters: { ...DEFAULT_COMMUNITY_GALLERY_FILTERS },
  // notificationPreferences
  notificationPreferences: { ...DEFAULT_NOTIFICATION_PREFERENCES },
}
